<script setup lang="ts">
import AgentIcon from 'src/shared/icons/agent.svg';
import BriefcaseIcon from 'src/shared/icons/briefcase.svg';
import BuilderIcon from 'src/shared/icons/builder.svg';
import MessageTextIcon from 'src/shared/icons/message-text-icon.svg';
import BigStar from 'src/shared/assets/big-star.svg';
import { Typography } from 'src/shared/ui/typography';

export type ParticipantType = 'agent' | 'agency' | 'developer';

withDefaults(
  defineProps<{
    userType: ParticipantType;
    reviewsCount: number;
    rating: number;
    compact?: boolean;
  }>(),
  { compact: false },
);

const roleLabels: Record<ParticipantType, string> = {
  agent: 'Агент',
  agency: 'Агентство',
  developer: 'Застройщик',
};

function formatRating(value: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value);
}
</script>

<template>
  <div :class="['participant-summary__meta', { 'participant-meta--compact': compact }]">
    <AgentIcon v-if="userType === 'agent'" class="participant-summary__role-icon" />
    <BriefcaseIcon v-else-if="userType === 'agency'" class="participant-summary__role-icon" />
    <BuilderIcon v-else class="participant-summary__role-icon" />
    <Typography
      :variant="compact ? 'body-4' : 'caption-2'"
      as="span"
      class="participant-summary__meta-text"
    >
      {{ roleLabels[userType] }}
    </Typography>
    <MessageTextIcon class="participant-summary__message-icon" />
    <Typography
      :variant="compact ? 'body-4' : 'caption-2'"
      as="span"
      class="participant-summary__meta-text"
    >
      {{ reviewsCount }}
    </Typography>
    <span class="participant-summary__rating-separator participant-summary__meta-text">•</span>
    <BigStar class="participant-summary__star" />
    <Typography
      :variant="compact ? 'body-4' : 'caption-2'"
      as="span"
      class="participant-summary__rating participant-summary__meta-text"
    >
      {{ formatRating(rating) }}
    </Typography>
  </div>
</template>

<style scoped>
.participant-summary__meta {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 3px;
  color: var(--palette-graphite);
}

.participant-summary__meta-text {
  font-size: 13px;
  line-height: 20px;
}

.participant-summary__role-icon,
.participant-summary__message-icon,
.participant-summary__star {
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
}

.participant-summary__message-icon {
  margin-left: 6px;
}

.participant-summary__star,
.participant-summary__rating {
  color: var(--palette-blue-light);
}

.participant-meta--compact .participant-summary__meta-text {
  font-size: 17px;
  line-height: 28px;
}

.participant-meta--compact .participant-summary__role-icon,
.participant-meta--compact .participant-summary__message-icon,
.participant-meta--compact .participant-summary__star {
  width: 18px;
  height: 18px;
  flex-basis: 18px;
}
</style>
